import { useEffect, useId } from "react";
import type { DialerQueue } from "@gioia365/contracts";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { messageForError } from "../i18n/errors";
import { useT, type Translate } from "../i18n/locale";
import { useDialerSession } from "./dialer-session";
import { dialerLeadLine, dialerLeadName, passoverText, skipReasonText } from "./dialer-texts";

// «La coda della sessione» (piano M5, Task 5; mock-up `scheda-lead.html`, `m-queue`): il Lead aperto, i prossimi nell'ordine
// della lista con il loro numero, quelli che si saltano da soli con il motivo, quelli già fatti con com'è andata. Si legge a ogni
// apertura (la coda è la lista riletta, D1); mentre è aperta la chiamata non parte.

type Worked = DialerQueue["worked"][number];

function workedText(t: Translate, item: Worked): string {
  const result =
    item.result === "called"
      ? t("dialer.queueResults.called")
      : item.result === "skipped"
        ? t("dialer.queueResults.skipped", {
            reason: skipReasonText(t, item.reason),
          })
        : t("dialer.queueResults.passed");
  return t("dialer.withReason", {
    lead: dialerLeadName(item.lead),
    reason: result,
  });
}

export function QueueDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const dialer = useDialerSession();
  const { hold, watchQueue } = dialer;
  const query = dialer.queue();
  const nowId = useId();
  const nextId = useId();
  const passedId = useId();
  const workedId = useId();

  useEffect(() => {
    watchQueue(true);
    const release = hold();
    return () => {
      watchQueue(false);
      release();
    };
  }, [hold, watchQueue]);

  const queue = query.data;
  // Dalla cascata dai Task la coda finisce con i Task, non con una lista (questione 339).
  const fromTasks = dialer.session?.source.type === "task_view";
  return (
    <Dialog open size="wide" title={t("dialer.queueTitle")} onClose={onClose}>
      {query.isError ? (
        <div className="g-stack">
          <p className="g-field__error" role="alert">
            {messageForError(t, query.error)}
          </p>
          <div>
            <Button onClick={() => void query.refetch()}>{t("common.retry")}</Button>
          </div>
        </div>
      ) : !queue ? (
        <p className="g-field__hint" role="status">
          {t("common.loading")}
        </p>
      ) : (
        <div className="g-dialer-queue">
          <section aria-labelledby={nowId}>
            <h3 id={nowId} className="g-dialer-queue__title">
              {t("dialer.queueNow")}
            </h3>
            <p>{queue.current ? dialerLeadLine(queue.current) : "—"}</p>
          </section>
          <section aria-labelledby={nextId}>
            <h3 id={nextId} className="g-dialer-queue__title">
              {t("dialer.queueNext")}
            </h3>
            {queue.upcoming.length > 0 ? (
              <ol className="g-dialer-queue__list">
                {queue.upcoming.map((lead) => (
                  <li key={lead.id} value={lead.position ?? undefined}>
                    {dialerLeadLine(lead)}
                  </li>
                ))}
              </ol>
            ) : (
              <p>{t(fromTasks ? "dialer.queueNoneTasks" : "dialer.queueNone")}</p>
            )}
          </section>
          {queue.passedOver.length > 0 ? (
            <section aria-labelledby={passedId}>
              <h3 id={passedId} className="g-dialer-queue__title">
                {t("dialer.queuePassed")}
              </h3>
              <ul className="g-dialer-queue__list">
                {queue.passedOver.map((lead) => (
                  <li key={lead.id}>
                    {lead.passover
                      ? t("dialer.withReason", {
                          lead: dialerLeadName(lead),
                          reason: passoverText(t, lead.passover, dialer.timeZone),
                        })
                      : dialerLeadName(lead)}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {queue.worked.length > 0 ? (
            <section aria-labelledby={workedId}>
              <h3 id={workedId} className="g-dialer-queue__title">
                {t("dialer.queueWorked")}
              </h3>
              <ul className="g-dialer-queue__list">
                {queue.worked.map((item) => (
                  <li key={`${item.lead.id}:${item.at}`}>{workedText(t, item)}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
